import React, { useCallback, useState } from 'react'
import { Redirect, useHistory } from 'react-router-dom'
import { useMutation } from 'react-query'
import { API_ERROR_CODES } from '@customTypes/Generic'
import { SlotStatus } from '@customTypes/Generic'

import { AppointmentConfirmationScreen } from '@screens/AppointmentConfirmation/AppointmentConfirmation'
import { setAfterBookingData, useConfig, useConfigDispatch } from '@context/ConfigurationContext'
import { useRescheduleDispatch, setSlotStatus } from '@context/RescheduleContext'
import { setAppointmentDuration, useChoiceDispatch, useChoiceState } from '@context/ChoiceContext'

import { useRecallQueryParams } from '@hooks/useQueryParams'
import useComponentDidMount from '@hooks/useComponentDidMount'
import useValidateSelectedSlot from '@hooks/useValidateSelectedSlot/useValidateSelectedSlot'

import routes from 'src/config/routes'
import { prepareAppointmentRescheduleSuccessUrl } from 'src/utils/prepareAppointmentRescheduleSuccessUrl'
import { createRecallBooking } from 'src/services/api/booking'
import {
  seenAppointmentConfirmation,
  completedRecallFlow,
  analyticsEventRescheduleMissingParam,
} from 'src/services/analytics'
import captureExceptionWithResponse from 'src/services/sentry'
import useTreatment from '@hooks/useTreatment'
import { RecallDetails } from '@components/Recall/AppointmentConfirmationDetails'
import { useAppointmentStart } from '@hooks/useAppointmentStart'
import { useTranslation } from 'react-i18next'
import { useRecommendedPractitioner } from '@hooks/useRecommendedPractitioner'
import { getErrorTranslationKey } from 'src/helpers/getApiErrorTranslationKey'
import { CreationEvent } from 'src/lib'
import { getAfterBookingStateFromBookingResponse } from 'src/utils/getAfterBookingStateFromBookingResponse'

const AppointmentConfirmationRecall = () => {
  const history = useHistory()
  const { clinicId, successUrl, treatmentBundleTypeReferenceId } = useConfig()
  const { selectedBookableDateAndTime, appointmentDuration } = useChoiceState()
  const [error, setError] = useState('')
  const [slotValidationError, setSlotValidationError] = useState<boolean>(false)
  const { sourceWebsiteType } = useConfig()
  const lang = useTranslation().i18n.language
  const rescheduleDispatch = useRescheduleDispatch()
  const { data: recommendedPractitioner } = useRecommendedPractitioner()
  const configDispatch = useConfigDispatch()
  const choiceDispatch = useChoiceDispatch()

  const { queryParams, hasAllRequiredParams } = useRecallQueryParams()

  useComponentDidMount(() => {
    seenAppointmentConfirmation()

    if (!hasAllRequiredParams) {
      captureExceptionWithResponse('Recall: Missing required query params', {
        queryParams,
        selectedBookableDateAndTime,
      })

      analyticsEventRescheduleMissingParam(queryParams)
    }

    if (!appointmentDuration) {
      choiceDispatch(setAppointmentDuration(parseInt(queryParams?.durationMin || '45', 10)))
    }
  })

  const appointmentStart = useAppointmentStart(queryParams?.start)

  // If there is not user selected slot it will validate the received from query params
  const { isLoading: isValidating, shouldRedirect } = useValidateSelectedSlot(
    { start: queryParams?.start, duration: queryParams?.durationMin },
    {
      onError: () => {
        setSlotValidationError(true)
      },
    }
  )

  const { data: treatment } = useTreatment(clinicId, treatmentBundleTypeReferenceId)

  const { mutate: confirmAppointment, isLoading: isProcessing } = useMutation(createRecallBooking, {
    onSuccess: async (response) => {
      configDispatch(setAfterBookingData(getAfterBookingStateFromBookingResponse(response.data)))

      completedRecallFlow()
      if (successUrl) {
        window.location.href = prepareAppointmentRescheduleSuccessUrl(successUrl, appointmentStart)
        return
      }

      history.push(routes.success)
    },
    onError: (err: Record<string, any>) => {
      rescheduleDispatch(setSlotStatus(SlotStatus.NOT_AVAILABLE))
      if (
        err.response?.data?.errorCode === API_ERROR_CODES.BOOKING_SLOT_GONE ||
        err.response?.data?.errorCode === API_ERROR_CODES.INVALID_REQUEST_PARAM_VALUE
      ) {
        return history.push(routes.appointment)
      }

      setError(getErrorTranslationKey(err))
    },
  })

  const processBooking = useCallback(() => {
    confirmAppointment({
      clinicId,
      booking: {
        treatmentBundleTypeReferenceId: queryParams?.treatmentBundleTypeReferenceId as string,
        customerId: queryParams?.customerId as string,
        start: appointmentStart as string,
        preferredPractitionerReferenceId: recommendedPractitioner?.referenceId || queryParams?.practitionerReferenceId,
        creationSource: sourceWebsiteType,
        creationEvent: CreationEvent.RECALL,
      },
      trackingParams: queryParams,
      lang,
    })
  }, [
    confirmAppointment,
    clinicId,
    queryParams,
    appointmentStart,
    recommendedPractitioner?.referenceId,
    lang,
    sourceWebsiteType,
  ])

  const handleNextClicked = () => {
    return processBooking()
  }

  const handleBackClicked = () => {
    history.push(routes.appointment)
  }

  if (shouldRedirect) {
    return <Redirect to={routes.appointment} />
  }

  return (
    <>
      <AppointmentConfirmationScreen
        isValidating={isValidating}
        onNextClicked={handleNextClicked}
        onBackClicked={handleBackClicked}
        isProcessing={isProcessing}
        error={error}
        showGenericError={slotValidationError || !hasAllRequiredParams}
      >
        {appointmentStart ? <RecallDetails treatment={treatment} selectedSlot={new Date(appointmentStart)} /> : null}
      </AppointmentConfirmationScreen>
    </>
  )
}

export default AppointmentConfirmationRecall
